import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import LoadingIndicator from '@/components/LoadingIndicator';
import { useFetchCleanUpItems } from '@/pages/Organizations/Organizations.store';

import { useOrganizationDialog } from './OrganizationEditDialog.hooks';
import type {
  OrganizationEditDialogForm,
  OrganizationNewInSFDTO
} from './OrganizationEditDialog.types';
import { OrganizationEditDialogContentCleanUp } from './OrganizationEditDialogContentCleanUp';
import { OrganizationEditDialogContentEdit } from './OrganizationEditDialogContentEdit';
import { OrganizationEditDialogContentNew } from './OrganizationEditDialogContentNew';

export const OrganizationEditDialog = () => {
  const { error, mutation, open, organization, mode, closeDialog } =
    useOrganizationDialog();

  const { data: cleanUpItems } = useFetchCleanUpItems(
    mode === 'CleanUp' && organization.id > 0 ? organization.id : 0
  );

  const hasActiveProducts = useMemo(() => {
    return (organization.products || []).some((p) =>
      dayjs(p.end_date).isAfter(dayjs())
    );
  }, [organization.products]);

  const methods = useForm<OrganizationEditDialogForm>({
    defaultValues: {
      id: organization.id,
      name: organization.name,
      label: organization.label
    },
    mode: 'onChange'
  });

  const {
    isFetching,
    data: sfOrganizations,
    isSuccess
  } = useQuery<OrganizationNewInSFDTO[]>({
    queryKey: ['organizations/sf'],
    enabled: mode === 'New'
  });

  useEffect(() => {
    if (isSuccess && sfOrganizations.length > 0 && organization.id === -1) {
      const { id, name, short_code: label } = sfOrganizations[0];
      methods.setValue('id', id);
      methods.setValue('name', name);
      methods.setValue('label', label || '');
    }
  }, [isSuccess, sfOrganizations, organization.id, methods]);

  const onSubmit = useCallback(
    (org: OrganizationEditDialogForm) => mutation.mutate(org),
    [mutation]
  );

  const title = useMemo(() => {
    let titleStr = `Create New Organization`;
    if (mode === 'Demo') {
      titleStr = 'Create Demo Organization';
    }
    if (mode === 'Delete') {
      titleStr = 'Delete Organization';
    }
    if (mode === 'Edit') {
      titleStr = `Edit Organization (ID ${organization.id})`;
    }
    if (mode === 'SalesforceSync') {
      titleStr = 'Sync Salesforce Organizations';
    }
    if (mode === 'CleanUp') {
      titleStr = 'Clean Up Organization';
    }
    return titleStr;
  }, [mode, organization.id]);

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ minHeight: '12rem' }}>
        <FormProvider {...methods}>
          {isFetching && <LoadingIndicator isLoading />}

          {mode === 'New' && !isFetching && (
            <OrganizationEditDialogContentNew
              organizations={sfOrganizations || []}
            />
          )}

          {(mode === 'Demo' || mode === 'Edit') && (
            <OrganizationEditDialogContentEdit />
          )}

          {mode === 'Delete' && !hasActiveProducts && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete the organization{' '}
                <strong>{organization.name}?</strong>
              </Typography>
              <Typography variant="body1">
                Deleting an organization will also delete all associated data
                like tickets, reports, etc.
              </Typography>
            </>
          )}

          {mode === 'Delete' && hasActiveProducts && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Organization with active products cannot be deleted.
            </Alert>
          )}

          {mode === 'SalesforceSync' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Syncing Salesforce organizations will take some time. Try creating
              a new organization a few minutes after triggering this sync.
            </Alert>
          )}
          {mode === 'CleanUp' && (
            <OrganizationEditDialogContentCleanUp repos={cleanUpItems || []} />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        {(mode !== 'New' ||
          (mode === 'New' && (sfOrganizations || []).length > 0)) && (
          <Button
            variant="contained"
            color={mode === 'Delete' ? 'error' : 'primary'}
            onClick={methods.handleSubmit(onSubmit)}
            disabled={
              mutation.isPending || (mode === 'Delete' && hasActiveProducts)
            }
            sx={{ position: 'relative', minWidth: '5rem' }}
          >
            {!mutation.isPending && (
              <span>
                {mode === 'Edit' && 'Save'}
                {mode === 'New' && 'Create'}
                {mode === 'Delete' && 'Delete'}
                {mode === 'Demo' && 'Create'}
                {mode === 'SalesforceSync' && 'Sync'}
                {mode === 'CleanUp' && 'Clean Up'}
              </span>
            )}
            {mutation.isPending && (
              <CircularProgress color="secondary" size={20} />
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationEditDialog;
